import { useEffect, useState } from 'react';

import { ExternalLink, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import useOutlookConnection, {
  getOutlookConnectionError,
} from '@/features/settings/queries/hooks/useOutlookConnection';

import type { ModalProps } from '../providers/ModalProvider/ModalProvider';
import { Button } from '../shadcn/button';

import PlatformLink from './PlatformLink';

const CONNECTION_POLL_INTERVAL_MS = 2000;

export default function OutlookConnectionFailedModal({ onClose }: ModalProps) {
  const { t } = useTranslation();
  const [hasOpenedConnectPage, setHasOpenedConnectPage] = useState(false);

  const { data: outlookConnection } = useOutlookConnection({
    refetchInterval: hasOpenedConnectPage ? CONNECTION_POLL_INTERVAL_MS : false,
  });

  const connectionError = getOutlookConnectionError(outlookConnection);

  useEffect(() => {
    if (!connectionError) onClose();
  }, [connectionError, onClose]);

  return (
    <div className="relative flex flex-col gap-4 p-4">
      <Button
        className="absolute top-2 right-2"
        variant="ghost"
        size="icon-xs"
        onClick={onClose}
      >
        <X />
      </Button>

      <div className="flex flex-col gap-2">
        <h2>{t('OUTLOOK_CONNECTION_FAILED_TITLE')}</h2>

        <div className="rounded bg-red-100 p-2 text-sm text-red-600 dark:bg-red-950 dark:text-red-300">
          {connectionError}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <Button asChild className="bg-[#0075DA] hover:bg-[#115EA3]">
          <PlatformLink
            to="/settings/connections/external"
            target="_blank"
            onClick={() => setHasOpenedConnectPage(true)}
          >
            {hasOpenedConnectPage && <Loader2 className="animate-spin" />}

            {t('OUTLOOK_RECONNECT_BUTTON')}

            <ExternalLink />
          </PlatformLink>
        </Button>

        <Button variant="ghost" onClick={onClose}>
          {t('OUTLOOK_REMINDER_LATER_BUTTON')}
        </Button>
      </div>
    </div>
  );
}
